import { useCallback, useMemo } from 'react';
import { type MessageDescriptor, useIntl } from 'react-intl';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import { idMeConfig, type useIdMeVerification } from '../../../../integrations/idme';
import { groupNameMessages, messages } from './useIdMeVerificationContent.messages';

import type { Asset } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const useIdMeVerificationContent = () => {
  const { data, fetching } = useContentfulContentTypeEntry<IdMeVerificationScreenFields>({
    contentType: 'idMeVerificationScreen',
  });

  const { formatMessage } = useIntl();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const fields = data?.fields;
  const contentGroupNames = fields?.groupNames;
  const verifiedMessage = fields?.verifiedMessage;
  const verifiedMultipleGroupsMessage = fields?.verifiedMultipleGroupsMessage;

  const getGroupName = useCallback(
    (verifiedGroup: VerifiedGroup) => {
      const { group } = verifiedGroup;

      const contentGroupName = contentGroupNames?.[group];
      const groupNameMessage = GROUP_NAME_MESSAGES[group];

      if (typeof contentGroupName === 'string' && contentGroupName.length > 0) {
        return contentGroupName;
      }

      if (groupNameMessage) {
        return formatMessage(groupNameMessage);
      }

      return group;
    },
    [contentGroupNames, formatMessage],
  );

  const getGroupNames = useCallback(
    (verifiedGroups: VerifiedGroups) => verifiedGroups.map(getGroupName),
    [getGroupName],
  );

  const getVerifiedDescription = useCallback(
    (groupNames: readonly string[]) => {
      const groups = groupNames.join(', ');
      const hasMultipleGroups = groupNames.length > 1;

      const template = hasMultipleGroups ? verifiedMultipleGroupsMessage : verifiedMessage;
      const fallbackMessage = hasMultipleGroups
        ? messages.verifiedMultipleGroupsMessage
        : messages.verifiedMessage;

      if (template) {
        return fillGroupsPlaceholder({ template, groups });
      }

      return formatMessage(fallbackMessage, { groups });
    },
    [formatMessage, verifiedMessage, verifiedMultipleGroupsMessage],
  );

  const partnerLogoFields = fields?.partnerLogo?.fields;
  const partnerLogoUrl = partnerLogoFields?.file?.url;
  const partnerLogoAlt = partnerLogoFields?.description || formatMessage(messages.partnerLogoAlt);

  const partnerLogo = useMemo(
    () => (partnerLogoUrl ? { url: partnerLogoUrl, alt: partnerLogoAlt } : undefined),
    [partnerLogoUrl, partnerLogoAlt],
  );

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isLoading: fetching && !data,
    title: fields?.title ?? formatMessage(messages.title),
    description:
      fields?.description ??
      formatMessage(messages.description, { scope: idMeConfig.scopes.join(', ') }),
    disclaimer: fields?.disclaimer,
    partnerLogo,
    learnMoreLabel: fields?.learnMoreLabel ?? formatMessage(messages.learnMoreLabel),
    learnMoreUrl: fields?.learnMoreUrl,
    switchAccountLabel: fields?.switchAccountLabel ?? formatMessage(messages.switchAccountLabel),
    cancelledMessage: fields?.cancelledMessage ?? formatMessage(messages.cancelledMessage),
    notVerifiedMessage: fields?.notVerifiedMessage ?? formatMessage(messages.notVerifiedMessage),
    failedMessage: fields?.failedMessage ?? formatMessage(messages.failedMessage),
    idMeAccountLinkedMessage: fields?.idMeAccountLinkedMessage,
    sweetgreenAccountLinkedMessage: fields?.sweetgreenAccountLinkedMessage,
    getGroupNames,
    getVerifiedDescription,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fillGroupsPlaceholder(params: FillGroupsPlaceholderParams) {
  const { template, groups } = params;

  return template.replace(GROUPS_PLACEHOLDER_PATTERN, groups);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUPS_PLACEHOLDER_PATTERN = /\{groups\}/g;

const GROUP_NAME_MESSAGES: Partial<Record<string, MessageDescriptor>> = {
  military: groupNameMessages.military,
  responder: groupNameMessages.responder,
  student: groupNameMessages.student,
  teacher: groupNameMessages.teacher,
  government: groupNameMessages.government,
  employee: groupNameMessages.employee,
  hospital_employee: groupNameMessages.hospitalEmployee,
  alumni: groupNameMessages.alumni,
  nurse: groupNameMessages.nurse,
  medical: groupNameMessages.medical,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type VerifiedGroups = ReturnType<typeof useIdMeVerification>['verifiedGroups'];
type VerifiedGroup = VerifiedGroups[number];

type FillGroupsPlaceholderParams = {
  template: string;
  groups: string;
};

type IdMeVerificationScreenFields = {
  title?: string;
  description?: string;
  disclaimer?: string;
  partnerLogo?: Asset;
  learnMoreLabel?: string;
  learnMoreUrl?: string;
  switchAccountLabel?: string;
  cancelledMessage?: string;
  notVerifiedMessage?: string;
  failedMessage?: string;
  idMeAccountLinkedMessage?: string;
  sweetgreenAccountLinkedMessage?: string;
  verifiedMessage?: string;
  verifiedMultipleGroupsMessage?: string;
  groupNames?: Partial<Record<string, unknown>>;
};
